import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formatCurrency } from '../utils/currency';
import Confetti from 'react-confetti';

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(true);
  const [hotRaffles, setHotRaffles] = useState([]);
  const [loadingRaffles, setLoadingRaffles] = useState(true);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  useEffect(() => {
    const fetchHotRaffles = async () => {
      try {
        setLoadingRaffles(true);
        let rafflesData = [];
        try {
          const q = query(
            collection(db, 'raffles'),
            where('status', '==', 'active'),
            orderBy('createdAt', 'desc'),
            limit(4)
          );
          const snapshot = await getDocs(q);
          rafflesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (indexErr) {
          // Fallback without orderBy if composite index not ready
          console.warn('Index not ready, using fallback:', indexErr.message);
          const fallbackQ = query(
            collection(db, 'raffles'),
            where('status', '==', 'active')
          );
          const fallbackSnap = await getDocs(fallbackQ);
          rafflesData = fallbackSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
            .slice(0, 4);
        }
        setHotRaffles(rafflesData);
      } catch (error) {
        console.error('Error fetching hot raffles:', error);
        setHotRaffles([]);
      } finally {
        setLoadingRaffles(false);
      }
    };

    fetchHotRaffles();
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleRaffleClick = (raffle) => {
    const path = raffle.slug ? `/rifa/${raffle.slug}` : `/raffle/${raffle.id}`;
    navigate(path);
  };

  // getCurrencySymbol removed — use formatCurrency() instead

  return (
    <div className="home-page">
      {showConfetti && (
        <Confetti width={windowSize.width} height={windowSize.height} numberOfPieces={200} gravity={0.3} />
      )}

      {/* Hero Section */}
      <section className="hero">
        <div className="floating-icons">
          <span className="floating-icon">🎰</span>
          <span className="floating-icon">🎰</span>
          <span className="floating-icon">🎉</span>
          <span className="floating-icon">🏆</span>
          <span className="floating-icon">💰</span>
        </div>

        <div className="hero-content">
          <img src="/logo-white.png" alt="RifaMaker" style={{ height: '120px', marginBottom: '1rem' }} />
          <h1>{t('hero.title')}</h1>
          <p>{t('hero.subtitle')}</p>
          <div className="hero-buttons">
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/raffles')}>
              {t('hero.cta')}
            </button>
            <button className="btn btn-secondary btn-lg" onClick={() => navigate('/create')}>
              {t('hero.createCta')}
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <h2>{t('features.title')}</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">⚡</div>
            <h3>{t('features.easy')}</h3>
            <p>{t('features.easyDesc')}</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🔒</div>
            <h3>{t('features.secure')}</h3>
            <p>{t('features.secureDesc')}</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🎉</div>
            <h3>{t('features.fun')}</h3>
            <p>{t('features.funDesc')}</p>
          </div>
        </div>
      </section>

      {/* Hot Raffles Section */}
      <section className="raffles-section">
        <h2>🔥 {t('nav.raffles')}</h2>
        {loadingRaffles ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <div className="spinner" style={{
              border: '4px solid var(--primary-light)',
              borderTop: '4px solid var(--primary)',
              borderRadius: '50%', width: '40px', height: '40px',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        ) : hotRaffles.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎰</div>
            <h3>{t('myRaffles.noRafflesYet')}</h3>
            <button className="btn btn-primary" onClick={() => navigate('/create')} style={{ marginTop: '1rem' }}>
              {t('raffle.create')}
            </button>
          </div>
        ) : (
          <div className="raffles-grid">
            {hotRaffles.map((raffle) => (
              <div key={raffle.id} className="raffle-card" onClick={() => handleRaffleClick(raffle)}>
                <div className="raffle-card-image">
                  {raffle.imageUrl ? (
                    <img src={raffle.imageUrl} alt={raffle.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span>{raffle.emoji || '🎰'}</span>
                  )}
                  <span className="raffle-card-badge badge-active">
                    {t('raffle.active')}
                  </span>
                </div>
                <div className="raffle-card-body">
                  <h3>{raffle.title}</h3>
                  <p>{raffle.description ? raffle.description.replace(/[#*_~`>|\-\[\]()!]/g, '').substring(0, 150) : ''}</p>
                  <div className="raffle-card-meta">
                    <span className="raffle-price">
                      {formatCurrency(raffle.ticketPrice, raffle.currency || 'BRL')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Home;
